import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Search, Upload, Trash2, ExternalLink, Filter } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
  { v: "sop", l: "SOP" },
  { v: "safety", l: "Safety" },
  { v: "regulation", l: "Regulation" },
  { v: "manual", l: "Manual" },
  { v: "checklist", l: "Checklist" },
];
const ASSET_CATS = [
  { v: "industrial_equipment", l: "Industrial Equipment" },
  { v: "vehicle", l: "Vehicle" },
  { v: "generic", l: "Generic" },
];

export default function Documents({ embedded = false, assetCategory = null }) {
  const { user } = useAuth();
  const canManage = ["admin","super_admin"].includes(user?.role) || user?.role === "approver";
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [assetCat, setAssetCat] = useState(assetCategory || "all");
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "sop", tags: "", applicable_asset_categories: [], file_id: "", file_name: "", content_type: "" });

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (cat !== "all") params.category = cat;
    if (assetCat !== "all") params.asset_category = assetCat;
    try { const { data } = await api.get("/documents", { params }); setDocs(data); }
    catch { toast.error("Failed to load documents"); }
  };
  useEffect(() => { load(); }, [cat, assetCat]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/uploads/photo`, { method: "POST", body: fd, credentials: "include" });
      const data = await res.json();
      if (data.file_id) {
        setForm({ ...form, file_id: data.file_id, file_name: file.name, content_type: file.type });
        toast.success("File uploaded — fill metadata");
      } else toast.error("Upload failed");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!form.title || !form.file_id) return toast.error("Title and file required");
    try {
      await api.post("/documents", {
        title: form.title, description: form.description, category: form.category,
        tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
        applicable_asset_categories: form.applicable_asset_categories,
        file_id: form.file_id, file_name: form.file_name, content_type: form.content_type,
      });
      toast.success("Document added");
      setOpen(false);
      setForm({ title: "", description: "", category: "sop", tags: "", applicable_asset_categories: [], file_id: "", file_name: "", content_type: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this document?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const view = async (d) => {
    // Trigger view_count increment then open the file
    try { await api.get(`/documents/${d.id}`); } catch (err) { console.debug("view-count increment failed", err?.message); }
    window.open(`${API}/uploads/${d.file_id}`, "_blank");
  };

  const toggleAssetCat = (v) => {
    const cur = form.applicable_asset_categories;
    setForm({ ...form, applicable_asset_categories: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v] });
  };

  return (
    <div className={embedded ? "" : "p-6 md:p-8"} data-testid="documents-page">
      {!embedded && (
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Knowledge Base</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Documents</h1>
            <p className="text-sm text-gray-500 mt-1">SOPs, safety instructions, regulations and manuals inspectors can consult during their work.</p>
          </div>
          {canManage && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="upload-doc-btn" className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]"><Upload className="w-4 h-4 mr-2" />Upload Document</Button>
              </DialogTrigger>
              <DialogContent className="rounded-md max-w-lg">
                <DialogHeader><DialogTitle className="font-display">Upload Document</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">File *</Label>
                    <label className="mt-1 flex items-center justify-center border-2 border-dashed border-gray-300 p-6 cursor-pointer hover:border-[#01ADEF] transition-colors" data-testid="doc-file-input">
                      <input type="file" className="hidden" onChange={upload} disabled={uploading} />
                      <div className="text-center">
                        {uploading ? <div className="text-sm text-[#01ADEF]">Uploading…</div> :
                         form.file_id ? <div className="text-sm text-[#059669] font-mono">{form.file_name}</div> :
                         <><Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" /><div className="text-sm text-gray-600">Click to choose file (PDF, image, etc.)</div></>}
                      </div>
                    </label>
                  </div>
                  <Field label="Title *"><Input data-testid="doc-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-md" /></Field>
                  <Field label="Description"><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-md" /></Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Category"><Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}><SelectTrigger className="rounded-md"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select></Field>
                    <Field label="Tags (comma-separated)"><Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="loler, iso-9001" className="rounded-md" /></Field>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Applies to Asset Categories</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {ASSET_CATS.map(a => (
                        <button key={a.v} type="button" data-testid={`doc-asset-cat-${a.v}`} onClick={() => toggleAssetCat(a.v)}
                          className={`text-xs uppercase tracking-wider font-bold px-3 py-1.5 border transition-colors ${form.applicable_asset_categories.includes(a.v) ? "bg-[#01ADEF] text-white border-[#01ADEF]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}>
                          {a.l}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Leave empty to show for all asset categories.</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button data-testid="save-doc-btn" onClick={save} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      )}

      {!embedded && (
        <div className="bg-white border border-gray-200 p-4 flex flex-wrap items-end gap-4 mb-4">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Search</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input data-testid="doc-search-input" placeholder="Search title, description, tags…" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} className="pl-9 rounded-md" />
            </div>
          </div>
          <div className="w-44">
            <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Category</Label>
            <Select value={cat} onValueChange={setCat}><SelectTrigger className="mt-1 rounded-md" data-testid="doc-filter-cat"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="w-52">
            <Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">Asset Category</Label>
            <Select value={assetCat} onValueChange={setAssetCat}><SelectTrigger className="mt-1 rounded-md" data-testid="doc-filter-asset"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem>{ASSET_CATS.map(a => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
      )}

      <div className={embedded ? "space-y-2" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"} data-testid="documents-list">
        {docs.length === 0 ? (
          <div className={`${embedded ? "" : "col-span-full"} bg-white border border-gray-200 p-8 text-center text-sm text-gray-500`}>
            <Filter className="w-6 h-6 text-gray-300 mx-auto mb-2" /> No documents found.
          </div>
        ) : docs.map(d => (
          <div key={d.id} className="bg-white border border-gray-200 p-4 flex items-start gap-3 hover:border-gray-300 transition-colors" data-testid={`doc-${d.id}`}>
            <div className="w-10 h-10 bg-[#F0F7FF] border border-[#DBEAFE] flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-[#01ADEF]" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-sm truncate">{d.title}</div>
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border bg-gray-100 border-gray-300 text-gray-700">{d.category}</span>
              </div>
              {d.description && <div className="text-xs text-gray-500 mt-1 line-clamp-2">{d.description}</div>}
              {(d.tags && d.tags.length > 0) && <div className="flex gap-1 mt-2 flex-wrap">{d.tags.map(t => <span key={t} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 font-mono">#{t}</span>)}</div>}
              <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-500 uppercase tracking-wider">
                <span>{d.uploader_name || "—"}</span>
                <span>·</span>
                <span>{d.view_count || 0} views</span>
              </div>
              <div className="flex items-center gap-3 mt-2">
                <button onClick={() => view(d)} data-testid={`view-doc-${d.id}`} className="text-xs uppercase tracking-wider font-semibold text-[#01ADEF] hover:text-[#0198D6] flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" />Open</button>
                {canManage && <button onClick={() => remove(d.id)} className="text-gray-400 hover:text-[#DC2626]"><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">{label}</Label><div className="mt-1">{children}</div></div>);
